"use client";

import { useTranslations } from "next-intl";
import Image, { StaticImageData } from "next/image";
import { Link } from "@/navigation";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";

// 图片静态导入，获得 Next.js Image 优化（自动压缩、尺寸推断、模糊占位图）
import imgCncMachining from "@/../public/image/new-version/home/solutions/cnc-machining.png";
import imgInjectionMolding from "@/../public/image/new-version/home/solutions/injection-molding.png";
import imgSheetMetal from "@/../public/image/new-version/home/solutions/sheet-metal-fabrication.png";
import img3dPrinting from "@/../public/image/new-version/home/solutions/3d-printing.png";
import imgExtrusion from "@/../public/image/new-version/home/solutions/extrustion2.png";
import imgDieCasting from "@/../public/image/new-version/home/solutions/die-casting.png";
import imgVacuumCasting from "@/../public/image/new-version/home/solutions/vacuum-casting.png";
import imgGearMachining from "@/../public/image/new-version/home/solutions/gear-machining.png";

export interface ServiceItem {
	titleKey: string;
	descKey: string;
	image: StaticImageData | string;
	href: string;
	linkTextKey?: string;
	/** 为 true 时底部链接改为按钮，点击打开悬浮报价表单，图片区域不跳转 */
	openModal?: boolean;
}

// 默认服务列表（首页使用）
const DEFAULT_SERVICES: ServiceItem[] = [
	{
		titleKey: "cncMachiningTitle",
		descKey: "cncMachiningDesc",
		image: imgCncMachining,
		href: "/cnc-machining",
	},
	{
		titleKey: "injectionMoldingTitle",
		descKey: "injectionMoldingDesc",
		image: imgInjectionMolding,
		href: "/injection-molding",
	},
	{
		titleKey: "sheetMetalTitle",
		descKey: "sheetMetalDesc",
		image: imgSheetMetal,
		href: "/sheet-metal-fabrication",
	},
	{
		titleKey: "printing3dTitle",
		descKey: "printing3dDesc",
		image: img3dPrinting,
		href: "/3d-printing",
	},
	{
		titleKey: "extrusionTitle",
		descKey: "extrusionDesc",
		image: imgExtrusion,
		href: "/#",
	},
	{
		titleKey: "dieCastingTitle",
		descKey: "dieCastingDesc",
		image: imgDieCasting,
		href: "/#",
	},
	{
		titleKey: "vacuumCastingTitle",
		descKey: "vacuumCastingDesc",
		image: imgVacuumCasting,
		href: "/#",
	},
	{
		titleKey: "gearMachiningTitle",
		descKey: "gearMachiningDesc",
		image: imgGearMachining,
		href: "/gear-machining",
	},
];

// CNC Machining 页面服务列表
export const CNC_MACHINING_SERVICES: ServiceItem[] = [
	{
		titleKey: "cncMillingTitle",
		descKey: "cncMillingDesc",
		image: "/image/new-version/machining/cnc-machining/part/cnc-milling-services.png",
		href: "/cnc-milling",
		linkTextKey: "cncMillingLink",
	},
	{
		titleKey: "cncTurningTitle",
		descKey: "cncTurningDesc",
		image: "/image/new-version/machining/cnc-machining/part/cnc-turning-services.png",
		href: "/cnc-turning",
		linkTextKey: "cncTurningLink",
	},
	{
		titleKey: "fiveAxisTitle",
		descKey: "fiveAxisDesc",
		image: "/image/new-version/machining/cnc-machining/part/5-axis-cnc-machining-services.png",
		href: "/5-axis-cnc-machining",
		linkTextKey: "fiveAxisLink",
	},
	{
		titleKey: "precisionTitle",
		descKey: "precisionDesc",
		image: "/image/new-version/machining/cnc-machining/part/precision-machining-services.png",
		href: "/precision-machining",
		linkTextKey: "precisionLink",
	},
];

// Gear Machining 页面服务列表
export const GEAR_MACHINING_SERVICES: ServiceItem[] = [
	{
		titleKey: "gearHobbingTitle",
		descKey: "gearHobbingDesc",
		image: "/image/new-version/machining/gear-machining/services/gear-hobbing.jpg",
		href: "/upload",
		linkTextKey: "getQuote",
	},
	{
		titleKey: "gearShapingTitle",
		descKey: "gearShapingDesc",
		image: "/image/new-version/machining/gear-machining/services/gear-shaping.webp",
		href: "/upload",
		linkTextKey: "getQuote",
	},
	{
		titleKey: "gearMillingTitle",
		descKey: "gearMillingDesc",
		image: "/image/new-version/machining/gear-machining/services/cnc-gear-milling.webp",
		href: "/upload",
		linkTextKey: "getQuote",
	},
	{
		titleKey: "gearGrindingTitle",
		descKey: "gearGrindingDesc",
		image: "/image/new-version/machining/gear-machining/services/gear-grinding.webp",
		href: "/upload",
		linkTextKey: "getQuote",
	},
];

export interface ManufacturingSolutionsProps {
	translationNamespace?: string;
	services?: ServiceItem[];
	showLinkButton?: boolean;
	/** 图片是否使用 cover 模式（默认 contain） */
	imageCover?: boolean;
	/** 是否移除图片背景色（默认有灰色背景） */
	noImageBackground?: boolean;
}

export default function ManufacturingSolutions({
	translationNamespace = "manufacturingSolutions",
	services = DEFAULT_SERVICES,
	showLinkButton = false,
	imageCover = false,
	noImageBackground = false,
}: ManufacturingSolutionsProps) {
	const t = useTranslations(translationNamespace);
	const { openModalInquiry } = useModalInquiryContext();

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题区域 */}
				<div className="mx-auto mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-gray-900 md:text-base">{t("description")}</p>
				</div>

				{/* 服务卡片网格 */}
				<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-7 lg:grid-cols-4 lg:gap-8">
					{services.map((service, index) => (
						<div
							key={index}
							className="group flex flex-col overflow-hidden rounded-lg border-[2px] border-[#7d7d7d] bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
						>
							{/* 图片容器：openModal 时不跳转，用 div */}
							{service.openModal ? (
								<div
									className={`relative aspect-[4/2.6] w-full overflow-hidden ${
										noImageBackground ? "" : "bg-[#f5f5f5]"
									}`}
								>
									<Image
										src={service.image}
										alt={t(service.titleKey)}
										fill
										className={`transition-transform duration-300 group-hover:scale-105 ${
											imageCover ? "object-cover" : "object-contain"
										}`}
										sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
									/>
								</div>
							) : (
								<Link
									href={service.href}
									className={`relative aspect-[4/2.6] w-full overflow-hidden ${
										noImageBackground ? "" : "bg-[#f5f5f5]"
									}`}
								>
									<Image
										src={service.image}
										alt={t(service.titleKey)}
										fill
										className={`transition-transform duration-300 group-hover:scale-105 ${
											imageCover ? "object-cover" : "object-contain"
										}`}
										sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
									/>
								</Link>
							)}

							{/* 内容区域 */}
							<div className="flex flex-1 flex-col p-4 sm:p-5">
								<h3 className="mb-2 text-lg font-bold text-black sm:mb-3 sm:text-xl md:text-2xl">
									{t(service.titleKey)}
								</h3>
								<p className="mb-4 flex-1 text-sm leading-relaxed text-gray-900 md:text-base">
									{t(service.descKey)}
								</p>

								{/* 链接按钮：openModal 时用按钮打开悬浮表单 */}
								{showLinkButton &&
									service.linkTextKey &&
									(service.openModal ? (
										<button
											type="button"
											onClick={() => openModalInquiry()}
											className="inline-flex items-center gap-1 text-sm font-semibold text-themePrimary400 transition-colors hover:text-mainColor"
										>
											{t(service.linkTextKey)}
											<ArrowUpRight size={16} weight="bold" />
										</button>
									) : (
										<Link
											href={service.href}
											className="inline-flex items-center gap-1 text-sm font-semibold text-themePrimary400 transition-colors hover:text-mainColor"
										>
											{t(service.linkTextKey)}
											<ArrowUpRight size={16} weight="bold" />
										</Link>
									))}
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
